import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Result,
  Select,
  Space,
  Steps,
  Typography,
} from "antd";
import { validationService } from "../../services/validation.service";
import { Notice } from "../common/Notice";
import type { PhoneConflictInfo } from "../../types/validation.types";

interface ValidationUpgradeWizardProps {
  onLevelUp?: (level: "LEVEL_2") => void;
  initialPhone?: string;
}

type WizardStep = "phone-start" | "phone-conflict" | "phone-verify" | "phone-transfer-otp" | "done";
type ValidationChannel = "SMS";

const stepItems = [
  { key: "phone-start", title: "Telefone" },
  { key: "phone-verify", title: "Código" },
  { key: "done", title: "Concluído" },
];

export function ValidationUpgradeWizard({ onLevelUp, initialPhone }: ValidationUpgradeWizardProps) {
  const [step, setStep] = useState<WizardStep>("phone-start");
  const [phoneE164] = useState(initialPhone ?? "");
  const [channel, setChannel] = useState<ValidationChannel>("SMS");
  const [otpCode, setOtpCode] = useState("");
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [transferSessionId, setTransferSessionId] = useState<number | null>(null);
  const [transferOtpCode, setTransferOtpCode] = useState("");
  const [conflictInfo, setConflictInfo] = useState<PhoneConflictInfo | null>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number>(3);
  const [resendCount, setResendCount] = useState<number>(0);
  const [cooldownUntil, setCooldownUntil] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completedLevel, setCompletedLevel] = useState<"LEVEL_2" | null>(null);

  const isCooldownActive = useMemo(() => {
    if (!cooldownUntil) {
      return false;
    }

    return new Date(cooldownUntil).getTime() > Date.now();
  }, [cooldownUntil]);

  const currentStepIndex = useMemo(() => {
    const stepOrder = ["phone-start", "phone-verify", "done"];
    return stepOrder.indexOf(step);
  }, [step]);

  const registeredPhone = initialPhone ?? phoneE164;

  const isPhoneReady = /^\+55\d{2}\d{8,9}$/.test(registeredPhone);

  const handleStart = async () => {
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const result = await validationService.startPhoneValidation({
        phoneE164: registeredPhone,
        channel,
      });
      setSessionId(result.sessionId);
      setStep("phone-verify");
      setAttemptsRemaining(3);
      setResendCount(0);
      setCooldownUntil(null);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { error?: string; maskedOwnerEmail?: string } } };
      if (axiosErr?.response?.status === 409 && axiosErr.response.data?.error === "PHONE_ALREADY_CLAIMED") {
        setConflictInfo({
          code: "PHONE_ALREADY_CLAIMED",
          maskedOwnerEmail: axiosErr.response.data.maskedOwnerEmail ?? "***",
        });
        setStep("phone-conflict");
      } else {
        setErrorMessage("Não foi possível iniciar a validação por telefone.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // T016: "Use another number" — reset to phone-start
  const handleUseAnotherPhone = () => {
    setConflictInfo(null);
    setErrorMessage(null);
    setStep("phone-start");
  };

  // US2: Start phone transfer (user chose "validate anyway")
  const handleStartTransfer = async () => {
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      const result = await validationService.startPhoneTransfer({
        phoneE164: registeredPhone,
        channel,
      });
      setTransferSessionId(result.sessionId);
      setTransferOtpCode("");
      setStep("phone-transfer-otp");
    } catch {
      setErrorMessage("Não foi possível iniciar a transferência do telefone.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // FR-009: Redirect to forgot-password with masked email hint
  const handleRecoverAccess = () => {
    const hint = conflictInfo?.maskedOwnerEmail ?? "";
    window.location.href = `/forgot-password${hint ? `?hint=${encodeURIComponent(hint)}` : ""}`;
  };

  const handleTransferVerify = async () => {
    if (!transferSessionId) return;
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      const result = await validationService.verifyPhoneTransfer({
        sessionId: transferSessionId,
        otpCode: transferOtpCode,
      });
      if (result.transferred) {
        setCompletedLevel("LEVEL_2");
        setStep("done");
        onLevelUp?.("LEVEL_2");
      }
    } catch {
      setErrorMessage("Código inválido ou expirado. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async () => {
    if (!sessionId) {
      return;
    }

    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const result = await validationService.verifyPhoneValidation({
        sessionId,
        otpCode,
      });

      setAttemptsRemaining(result.attemptsRemaining);
      setCooldownUntil(result.cooldownUntil ?? null);

      if (result.verified && result.validationLevel === "LEVEL_2") {
        setStep("done");
        setCompletedLevel(result.validationLevel);
        onLevelUp?.(result.validationLevel);
      } else if (result.verified && result.validationLevel === "LEVEL_3") {
        setErrorMessage("Nível 3 (documentação): Em breve.");
      }
    } catch {
      setErrorMessage("Código inválido ou expirado. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!sessionId || isCooldownActive) {
      return;
    }

    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const result = await validationService.resendPhoneValidation({
        sessionId,
        channel,
      });
      setResendCount(result.resendCount);
      setCooldownUntil(null);
    } catch {
      setErrorMessage("Limite de reenvio atingido. Aguarde para tentar novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === "done") {
    return (
      <Card bordered={false}>
        <Result
          status="success"
          title="Validação concluída"
          subTitle={`Seu perfil foi atualizado para ${completedLevel === "LEVEL_2" ? "o nível 2" : "o nível 2"}.`}
        />
      </Card>
    );
  }

  return (
    <Card bordered={false}>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <div>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>
            Aumentar nível de validação
          </Typography.Title>
          <Typography.Text type="secondary">
            Conclua as etapas abaixo para liberar pesquisas com maior exigência de confiança.
          </Typography.Text>
          <br />
          <Typography.Text type="secondary">
            Nível 3 (documentação): Em breve.
          </Typography.Text>
        </div>

        <Steps current={currentStepIndex} items={stepItems} size="small" />

        {errorMessage && <Alert type="error" showIcon message={errorMessage} />}

        {step === "phone-start" && (
          <Form layout="vertical" onFinish={handleStart}>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Telefone cadastrado">
                {registeredPhone || '-'}
              </Descriptions.Item>
            </Descriptions>

            {!isPhoneReady && (
              <Notice
                type="warning"
                showIcon
                message="Telefone inválido no cadastro"
                description="Atualize o telefone no perfil para o formato +55DDDNÚMERO antes de iniciar a validação."
              />
            )}

            <Form.Item label="Canal" required>
              <Select value={channel} onChange={(value) => setChannel(value as ValidationChannel)}>
                <Select.Option value="SMS">SMS</Select.Option>
              </Select>
            </Form.Item>

            <Button type="primary" htmlType="submit" loading={isSubmitting} disabled={!isPhoneReady}>
              Enviar código
            </Button>
          </Form>
        )}

        {step === "phone-conflict" && conflictInfo && (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Notice
              type="warning"
              showIcon
              message="Número já em uso"
              description={
                <>
                  Este número já está associado a outra conta (<strong>{conflictInfo.maskedOwnerEmail}</strong>).
                  O que deseja fazer?
                </>
              }
            />
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button block onClick={handleUseAnotherPhone}>
                Usar outro número
              </Button>
              <Button block type="primary" loading={isSubmitting} onClick={handleStartTransfer}>
                Validar mesmo assim (o outro usuário perderá este número)
              </Button>
              <Button block onClick={handleRecoverAccess}>
                Recuperar acesso à conta existente ({conflictInfo.maskedOwnerEmail})
              </Button>
            </Space>
          </Space>
        )}

        {step === "phone-transfer-otp" && (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Notice
              type="info"
              showIcon
              message="Confirme a transferência"
              description="Insira o código OTP recebido para confirmar a transferência do número para sua conta. O usuário anterior será notificado."
            />
            <Form layout="vertical" onFinish={handleTransferVerify}>
              <Form.Item label="Código OTP" required>
                <Input
                  value={transferOtpCode}
                  onChange={(event) => setTransferOtpCode(event.target.value)}
                  placeholder="123456"
                  maxLength={6}
                />
              </Form.Item>
              <Space wrap>
                <Button type="primary" htmlType="submit" loading={isSubmitting} disabled={!transferOtpCode.trim()}>
                  Confirmar transferência
                </Button>
                <Button onClick={handleUseAnotherPhone} disabled={isSubmitting}>
                  Cancelar
                </Button>
              </Space>
            </Form>
          </Space>
        )}

        {step === "phone-verify" && (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Tentativas restantes">{attemptsRemaining}</Descriptions.Item>
              <Descriptions.Item label="Reenvios">{resendCount}/3</Descriptions.Item>
              <Descriptions.Item label="Cooldown">
                {isCooldownActive ? "Ativo. Aguarde antes de reenviar ou validar novamente." : "Liberado"}
              </Descriptions.Item>
            </Descriptions>

            <Form layout="vertical" onFinish={handleVerify}>
              <Form.Item label="Código OTP" required>
                <Input
                  value={otpCode}
                  onChange={(event) => setOtpCode(event.target.value)}
                  placeholder="123456"
                  maxLength={6}
                />
              </Form.Item>

              <Space wrap>
                <Button type="primary" htmlType="submit" loading={isSubmitting} disabled={!otpCode.trim() || isCooldownActive}>
                  Validar código
                </Button>
                <Button onClick={handleResend} loading={isSubmitting} disabled={isCooldownActive}>
                  Reenviar código
                </Button>
              </Space>
            </Form>
          </Space>
        )}
      </Space>
    </Card>
  );
}
